/* eslint-disable max-lines */
import { useCallback, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { type ActorRefFrom } from "xstate";
import { transferArtworkMachine } from "@design-lab/entities/transfers-lab/model/transfer-artwork.machine";
import { transfersManagerAtom } from "@design-lab/entities/transfers-lab/model/transfers-manager.machine";
import { useDtfChannel } from "@design-lab/shared/hooks/useDtfChannel";
import { PRODUCT_ID } from "@design-lab/shared";
import {
  validateDesignLabArtworkFile,
  validateDesignLabArtworkFileType
} from "common/artwork-file-types";
import { useNotification } from "@design-lab/shared/ui/notification";
import { api, type models } from "jiffy-api";
import { objNamesToSnakeCase } from "common/utils/helpers/cases";
import type { Artwork } from "@design-lab/entities/artwork";
import { MAX_TRANSFER_ARTWORKS } from "@design-lab/entities/transfers-lab";
import {
  DEFAULT_TIMEOUT,
  LARGE_FILE_TIMEOUT,
  LARGE_FILE_THRESHOLD
} from "@design-lab/shared/constants";
import { useLibraryArtworkLoader } from "./useLibraryArtworkLoader";

export type TransferArtworkActor = ActorRefFrom<typeof transferArtworkMachine>;

export type TransferEntry = {
  id: string;
  ref: TransferArtworkActor;
  file: File | undefined;
  abortController: AbortController | undefined;
};

export function useTransfersUpload() {
  const [state, send] = useAtom(transfersManagerAtom);
  const { items, selectedId } = state.context;
  const { error: notifyError } = useNotification();
  const channel = useDtfChannel();

  // Create entries from machine items
  const entries: TransferEntry[] = items.map(item => ({
    id: item.id,
    ref: item,
    file: item.getSnapshot().context.file,
    abortController: undefined // We'll manage this separately
  }));

  // Store abort controllers separately using a ref to persist across renders
  const abortControllersRef = useRef(new Map<string, AbortController>());

  const selectEntry = useCallback(
    (id: string | null) => {
      send({ type: "artwork.select", id });
    },
    [send]
  );

  const removeEntry = useCallback(
    (id: string) => {
      // Abort any ongoing upload
      const controller = abortControllersRef.current.get(id);
      if (controller) {
        controller.abort();
        abortControllersRef.current.delete(id);
      }

      send({ type: "artwork.remove", id });
    },
    [send]
  );

  const cancelUpload = useCallback(
    (id: string) => {
      removeEntry(id);
    },
    [removeEntry]
  );

  const doUploadArtwork = useCallback(
    async (actorId: string, file: File, abortController: AbortController) => {
      try {
        const dtfChannelUuid = await channel.retrieveDtfChannelUuid();
        if (!dtfChannelUuid) {
          notifyError({ title: "Upload Failed", description: "DTF channel not available" });
          return;
        }

        const processTimeStamp = String(Date.now());

        const formData = new FormData();
        const params = objNamesToSnakeCase({
          file,
          productId: PRODUCT_ID,
          fileOriginalIndex: "0",
          dtfChannelUuid,
          preCut: "false",
          processTimeStamp,
          existingArtworkId: "",
          aiImageVersionId: "",
          communityImageId: ""
        });
        for (const [key, value] of Object.entries(params)) {
          if (key === "file") {
            formData.append(key, file);
          } else {
            formData.append(key, value as any);
          }
        }

        // Determine timeout based on file size
        const fileSize = file?.size || 0;
        const timeout = fileSize > LARGE_FILE_THRESHOLD ? LARGE_FILE_TIMEOUT : DEFAULT_TIMEOUT;

        const response = await api.dtf.uploadArtwork(formData, {
          raw: true,
          useAxiosForRequest: true,
          headers: { "Content-Type": undefined },
          signal: abortController.signal as any,
          timeout
        });

        const idRaw = (response as unknown as { id?: string | number }).id;
        if (idRaw !== undefined) {
          const idNum = typeof idRaw === "string" ? Number(idRaw) : (idRaw ?? 0);
          send({ type: "artwork.upload.completed", id: actorId, artworkId: idNum });
        }
      } catch (e: any) {
        // Swallow abort errors; notify on unexpected
        if (
          e?.name === "CanceledError" ||
          e?.message === "canceled" ||
          e?.code === "ERR_CANCELED"
        ) {
          return;
        }

        // Handle timeout errors specifically
        if (e?.code === "ECONNABORTED" || e?.response?.status === 504) {
          notifyError({
            title: "Upload Failed",
            description:
              "Upload timed out. The file may be taking too long to process or your connection may be slow. Please try again."
          });
          window.Honeybadger?.notify("Transfers upload timeout", {
            params: {
              error: "Request timeout",
              error_type: "timeout_error",
              hook: "useTransfersUpload",
              actorId,
              fileSize: file?.size || 0
            }
          });
          return;
        }

        window.Honeybadger?.notify("Transfers upload error", {
          params: {
            error: e instanceof Error ? e.message : String(e),
            error_object: e instanceof Error ? e.toString() : String(e),
            stack_trace: e instanceof Error ? e.stack : null,
            hook: "useTransfersUpload",
            actorId
          }
        });
        notifyError({ title: "Upload Failed", description: "An unexpected error occurred" });
      }
    },
    [channel, notifyError, send]
  );

  const addFiles = useCallback(
    async (files: File[], source: "upload" | "canva" = "upload") => {
      if (!files || files.length === 0) return;

      // Check if we've reached the MAX_TRANSFER_ARTWORKS artwork limit
      const remainingSlots = MAX_TRANSFER_ARTWORKS - items.length;
      if (remainingSlots <= 0) {
        // Silently ignore when at capacity
        return;
      }

      // Limit files to remaining slots - silently process only what fits (max MAX_TRANSFER_ARTWORKS)
      const filesToProcess = files.slice(0, Math.min(remainingSlots, MAX_TRANSFER_ARTWORKS));

      const validated: File[] = [];
      await Promise.all(
        filesToProcess.map(async file => {
          // Skip dimension validation for Canva sources as they may provide low-res previews
          // The backend will handle the actual high-res image
          if (source === "canva") {
            // Only validate file type for Canva sources
            const { valid, message } = validateDesignLabArtworkFileType(file);
            if (!valid) {
              notifyError({ title: "Upload Failed", description: `${file.name}: ${message}` });
            } else {
              validated.push(file);
            }
          } else {
            // Full validation including dimensions for other sources
            const { valid, message } = await validateDesignLabArtworkFile(file);
            if (!valid) {
              notifyError({ title: "Upload Failed", description: `${file.name}: ${message}` });
            } else {
              validated.push(file);
            }
          }
        })
      );

      if (validated.length === 0) return;

      validated.forEach(file => {
        // Add artwork through machine
        send({ type: "artwork.add", file, source });
      });
    },
    [notifyError, items.length, send]
  );

  // Handle uploads for new items
  useEffect(() => {
    items.forEach(item => {
      const snapshot = item.getSnapshot();
      const { file } = snapshot.context;

      // Check if this item needs upload and hasn't been started yet
      if (file && !snapshot.context.artworkId && !abortControllersRef.current.has(item.id)) {
        const abortController = new AbortController();
        abortControllersRef.current.set(item.id, abortController);

        // fire-and-forget upload
        doUploadArtwork(item.id, file, abortController).catch(() => {
          // Error already handled in doUploadArtwork
        });
      }
    });
  }, [items, doUploadArtwork]);

  // Route WebSocket messages to the machine
  useEffect(() => {
    const message = channel.lastMessage as models.cable.DtfChannel.Actions | undefined;
    if (message) {
      send({ type: "websocket.message", message });
    }
  }, [channel.lastMessage, send]);

  // Use the library artwork loader
  const handleArtworksLoaded = useCallback(
    (artworks: Artwork[]) => {
      artworks.forEach((artwork, index) => {
        // Add a small delay between adds to ensure proper ordering
        setTimeout(() => {
          // Convert string ID back to number for consistency
          const artworkId = typeof artwork.id === "string" ? parseInt(artwork.id, 10) : artwork.id;
          send({ type: "artwork.add", artworkId, artwork, source: "design_library" });
        }, index * 10);
      });
    },
    [send]
  );

  const { loadArtworks } = useLibraryArtworkLoader({
    onArtworksLoaded: handleArtworksLoaded
  });

  const addArtworksFromLibrary = useCallback(
    async (artworkIds: number[]) => {
      if (!artworkIds || artworkIds.length === 0) return;

      // Check if we've reached the MAX_TRANSFER_ARTWORKS artwork limit
      const remainingSlots = MAX_TRANSFER_ARTWORKS - items.length;
      if (remainingSlots <= 0) {
        // Silently ignore when at capacity
        return;
      }

      // Limit artworks to remaining slots
      const artworksToAdd = artworkIds.slice(0, Math.min(remainingSlots, MAX_TRANSFER_ARTWORKS));

      // Trigger the artwork loading - the loader will call handleArtworksLoaded when ready
      loadArtworks(artworksToAdd);
    },
    [items.length, loadArtworks]
  );

  return {
    entries,
    selectedId,
    hasUploadingArtwork: state.context.hasUploadingArtwork,
    uploadingCount: state.context.uploadingCount,
    addFiles,
    addArtworksFromLibrary,
    cancelUpload,
    removeEntry,
    selectEntry
  };
}
